import { type TemplateField } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { TemplateFieldEditor } from '@/components/features/ServiceRequests/TemplateFieldEditor';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface TemplateRecord {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    icon: string | null;
    category: string | null;
    is_active: boolean;
    sort_order: number;
    fields: TemplateField[];
    fields_count: number;
    service_requests_count: number;
}

const emptyForm = (): Omit<TemplateRecord, 'id' | 'fields_count' | 'service_requests_count'> => ({
    slug: '',
    name: '',
    description: '',
    icon: '',
    category: '',
    is_active: true,
    sort_order: 0,
    fields: [],
});

export function ServiceRequestTemplatesManager() {
    const { t } = useTranslations();
    const [templates, setTemplates] = useState<TemplateRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(emptyForm());
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await csrfFetch('/settings/system/service-request-templates');
            const data = await res.json();
            setTemplates(data.templates ?? []);
        } catch {
            toast.error(t('toasts.settings_servicerequesttemplatesmanager.load_failed', 'Failed to load templates.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchTemplates();
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setErrors({});
        setDialogOpen(true);
    };

    const openEdit = (t: TemplateRecord) => {
        setEditingId(t.id);
        setForm({
            slug: t.slug,
            name: t.name,
            description: t.description ?? '',
            icon: t.icon ?? '',
            category: t.category ?? '',
            is_active: t.is_active,
            sort_order: t.sort_order,
            fields: t.fields,
        });
        setErrors({});
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setErrors({});
        try {
            const url = editingId ? `/settings/system/service-request-templates/${editingId}` : '/settings/system/service-request-templates';
            const method = editingId ? 'PATCH' : 'POST';
            const res = await csrfFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();

            if (!res.ok) {
                if (data.errors) {
                    const flat: Record<string, string> = {};
                    for (const [k, v] of Object.entries(data.errors)) {
                        flat[k] = Array.isArray(v) ? (v[0] as string) : String(v);
                    }
                    setErrors(flat);
                } else {
                    toast.error(data.message ?? 'Failed to save template.');
                }
                return;
            }

            toast.success(
                editingId
                    ? t('toasts.settings_servicerequesttemplatesmanager.template_updated', 'Template updated.')
                    : t('toasts.settings_servicerequesttemplatesmanager.template_created', 'Template created.'),
            );
            setDialogOpen(false);
            await fetchTemplates();
        } catch {
            toast.error(t('toasts.settings_servicerequesttemplatesmanager.save_failed', 'Failed to save template.'));
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (id: string) => {
        if (!confirm('Archive this template? Existing submissions will be unaffected.')) return;
        try {
            await csrfFetch(`/settings/system/service-request-templates/${id}`, { method: 'DELETE' });
            toast.success(t('toasts.settings_servicerequesttemplatesmanager.template_archived', 'Template archived.'));
            await fetchTemplates();
        } catch {
            toast.error(t('toasts.settings_servicerequesttemplatesmanager.archive_failed', 'Failed to archive template.'));
        }
    };

    const setField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-muted-foreground text-sm">Templates define the fields users fill out when submitting a service request.</p>
                <Button size="sm" onClick={openCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Template
                </Button>
            </div>

            {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
            ) : templates.length === 0 ? (
                <Card>
                    <CardContent className="py-10 text-center">
                        <p className="text-muted-foreground text-sm">No templates yet. Create one to get started.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {templates.map((tpl) => (
                        <Card key={tpl.id} className="overflow-hidden">
                            <CardHeader className="px-4 py-3">
                                <div className="flex items-center justify-between gap-3">
                                    <button
                                        type="button"
                                        className="flex flex-1 items-center gap-3 text-left"
                                        onClick={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                                    >
                                        {expandedId === tpl.id ? (
                                            <ChevronUp className="text-muted-foreground h-4 w-4 shrink-0" />
                                        ) : (
                                            <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
                                        )}
                                        <div className="min-w-0">
                                            <CardTitle className="text-sm font-semibold">{tpl.name}</CardTitle>
                                            <p className="text-muted-foreground text-xs">{tpl.slug}</p>
                                        </div>
                                        <div className="ml-auto flex items-center gap-2">
                                            <Badge
                                                variant="outline"
                                                className={
                                                    tpl.is_active
                                                        ? 'bg-success-subtle text-success-subtle-foreground'
                                                        : 'bg-muted text-muted-foreground'
                                                }
                                            >
                                                {tpl.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                            <span className="text-muted-foreground text-xs">{tpl.fields_count} fields</span>
                                            <span className="text-muted-foreground text-xs">{tpl.service_requests_count} submissions</span>
                                        </div>
                                    </button>
                                    <div className="flex items-center gap-1">
                                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(tpl)}>
                                            <Pencil className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="text-destructive h-8 w-8"
                                            onClick={() => handleArchive(tpl.id)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            {expandedId === tpl.id && (
                                <CardContent className="border-t px-4 py-3">
                                    {tpl.description && <p className="text-muted-foreground mb-3 text-sm">{tpl.description}</p>}
                                    {tpl.fields.length === 0 ? (
                                        <p className="text-muted-foreground text-sm">No fields defined.</p>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {[...tpl.fields]
                                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                                .map((f) => (
                                                    <div key={f.key} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                                                        <span className="font-medium">{f.label}</span>
                                                        <Badge variant="outline" className="text-xs">
                                                            {f.type}
                                                        </Badge>
                                                        {f.required && (
                                                            <Badge
                                                                variant="outline"
                                                                className="bg-danger-subtle text-danger-subtle-foreground text-xs"
                                                            >
                                                                required
                                                            </Badge>
                                                        )}
                                                        <span className="text-muted-foreground ml-auto text-xs">key: {f.key}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </CardContent>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            <ResponsiveModal open={dialogOpen} onOpenChange={setDialogOpen} variant="sheet">
                <ResponsiveModalContent size="xl">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{editingId ? 'Edit Template' : 'New Template'}</ResponsiveModalTitle>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>Name *</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => {
                                        setField('name', e.target.value);
                                        if (!editingId && !form.slug) {
                                            setField(
                                                'slug',
                                                e.target.value
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9]+/g, '-')
                                                    .replace(/^-|-$/g, ''),
                                            );
                                        }
                                    }}
                                />
                                {errors.name && <p className="text-destructive text-xs">{errors.name}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <Label>Slug *</Label>
                                <Input value={form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="my-template-slug" />
                                {errors.slug && <p className="text-destructive text-xs">{errors.slug}</p>}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label>Description</Label>
                            <Textarea value={form.description ?? ''} onChange={(e) => setField('description', e.target.value)} rows={2} />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <Label>Icon</Label>
                                <Input value={form.icon ?? ''} onChange={(e) => setField('icon', e.target.value)} placeholder="e.g. Monitor" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Category</Label>
                                <Input
                                    value={form.category ?? ''}
                                    onChange={(e) => setField('category', e.target.value)}
                                    placeholder="e.g. hardware"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Sort Order</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    value={form.sort_order}
                                    onChange={(e) => setField('sort_order', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Checkbox id="is_active" checked={form.is_active} onCheckedChange={(v) => setField('is_active', Boolean(v))} />
                            <Label htmlFor="is_active" className="cursor-pointer">
                                Active
                            </Label>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-semibold">Fields</Label>
                            <TemplateFieldEditor fields={form.fields} onChange={(fields) => setField('fields', fields)} />
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Template'}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </div>
    );
}
